import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  CheckCircle2, Sparkles, Building2, Users, FileText, CreditCard,
  Mail, Shield, ArrowRight, SkipForward, PartyPopper,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * First-run experience. Visible the first time an admin user lands on
 * the dashboard after signup. 6 friendly steps the user can skip or
 * complete at their own pace. Completion is persisted so it never shows
 * again unless explicitly replayed from Settings.
 */
export default function OnboardingWizard({ open, onClose }) {
  const { user, setUser, getAuthHeaders } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [firmName, setFirmName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const lbl = (es, en) => (language === 'es' ? es : en);

  const totalSteps = 6;

  async function finish() {
    setSaving(true);
    try {
      await fetch(`${API}/auth/complete-onboarding`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (user) setUser({ ...user, onboarding_completed: true });
      toast.success(lbl('¡Listo! Bienvenido a ERP Legal', 'All set! Welcome to ERP Legal'));
      onClose?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function saveFirmName() {
    if (!firmName.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/tenant/branding`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ firm_name: firmName.trim() }),
      });
      if (!res.ok) {
        // Gracefully swallow: tenant might not exist yet, not fatal
      }
      next();
    } catch {
      next();
    } finally {
      setSaving(false);
    }
  }

  const next = () => setStep((s) => Math.min(s + 1, totalSteps - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const step0 = (
    <StepShell
      icon={<PartyPopper className="w-12 h-12 text-bronze" />}
      title={lbl(`¡Hola, ${(user?.name || '').split(' ')[0]}!`, `Hi, ${(user?.name || '').split(' ')[0]}!`)}
      lead={lbl(
        'Bienvenido a ERP Legal. En los proximos 2 minutos le mostraremos lo esencial para que pueda trabajar desde el primer dia.',
        `Welcome to ERP Legal. In the next 2 minutes we'll show you the essentials so you can work from day one.`
      )}
    >
      <div className="grid grid-cols-2 gap-3 mt-4">
        <Highlight icon={Building2} text={lbl('Personalice su bufete', 'Brand your firm')} />
        <Highlight icon={Users} text={lbl('Invite a su equipo', 'Invite your team')} />
        <Highlight icon={FileText} text={lbl('Cree su primer caso', 'Create your first case')} />
        <Highlight icon={Sparkles} text={lbl('Conozca el asistente IA', 'Meet the AI assistant')} />
      </div>
    </StepShell>
  );

  const step1 = (
    <StepShell
      icon={<Building2 className="w-12 h-12 text-bronze" />}
      title={lbl('Personalicemos su bufete', `Let's brand your firm`)}
      lead={lbl(
        'Empezaremos por lo mas visible: el nombre que aparecera en sus facturas y correos.',
        'Let us start with the most visible thing: the name that will show on your invoices and emails.'
      )}
    >
      <div className="space-y-2 mt-4">
        <Label htmlFor="onb-firm">{lbl('Nombre del bufete', 'Firm name')}</Label>
        <Input
          id="onb-firm"
          placeholder={lbl('Perez & Asociados', 'Smith & Associates')}
          value={firmName}
          onChange={(e) => setFirmName(e.target.value)}
          autoFocus
        />
        <p className="text-xs text-muted-foreground">
          {lbl(
            'Despues podra agregar logo, colores, RNC y direccion desde Ajustes → Marca.',
            'You can add logo, colors, RNC and address later from Settings → Branding.'
          )}
        </p>
      </div>
    </StepShell>
  );

  const step2 = (
    <StepShell
      icon={<Users className="w-12 h-12 text-bronze" />}
      title={lbl('Invite a su equipo cuando guste', 'Invite your team anytime')}
      lead={lbl(
        'ERP Legal trabaja mejor en equipo. Puede invitar asistentes, abogados junior, senior y otros administradores con distintos permisos.',
        'ERP Legal works best in teams. You can invite assistants, junior and senior lawyers, and other administrators with different permission levels.'
      )}
    >
      <div className="mt-4 rounded-md border border-border bg-card p-4 text-sm space-y-2">
        <p className="text-foreground font-medium">
          {lbl('Roles disponibles:', 'Available roles:')}
        </p>
        <ul className="text-muted-foreground space-y-1 list-disc pl-5">
          <li><strong>{lbl('Administrador', 'Administrator')}</strong> — {lbl('control total, facturacion, gestion de equipo', 'full control, billing, team management')}</li>
          <li><strong>{lbl('Abogado Senior', 'Senior Lawyer')}</strong> — {lbl('todos los casos, facturas, cuentas fiduciarias', 'all cases, invoices, trust accounts')}</li>
          <li><strong>{lbl('Abogado Junior', 'Junior Lawyer')}</strong> — {lbl('casos asignados, fiduciaria de solo lectura', 'assigned cases, trust read-only')}</li>
          <li><strong>{lbl('Asistente', 'Assistant')}</strong> — {lbl('tiempo, documentos, tareas de casos asignados', 'time, documents, tasks on assigned cases')}</li>
        </ul>
        <p className="text-xs text-muted-foreground mt-2">
          {lbl('Los puede invitar cuando quiera desde Equipo.', 'Invite them whenever you want from the Team page.')}
        </p>
      </div>
    </StepShell>
  );

  const step3 = (
    <StepShell
      icon={<FileText className="w-12 h-12 text-bronze" />}
      title={lbl('Todo gira alrededor de un caso', 'Everything revolves around a case')}
      lead={lbl(
        'Cada caso es un expediente: cliente, documentos, tiempo facturable, tareas, calendario, facturas y fideicomisos. Todo en un solo lugar.',
        'Each case is a file: client, documents, billable time, tasks, calendar, invoices and trust. All in one place.'
      )}
    >
      <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
        <Pill text={lbl('Cliente', 'Client')} />
        <Pill text={lbl('Documentos', 'Documents')} />
        <Pill text={lbl('Tiempo', 'Time')} />
        <Pill text={lbl('Tareas', 'Tasks')} />
        <Pill text={lbl('Calendario', 'Calendar')} />
        <Pill text={lbl('Facturas', 'Invoices')} />
      </div>
      <p className="text-sm text-muted-foreground mt-4">
        {lbl(
          'Cuando termine el wizard le llevaremos a Casos para que cree el primero. O puede saltarlo y crearlo cuando tenga uno real.',
          `When the wizard finishes we'll take you to Cases so you can create your first one. Or skip and create it when you have a real case.`
        )}
      </p>
    </StepShell>
  );

  const step4 = (
    <StepShell
      icon={<Sparkles className="w-12 h-12 text-bronze" />}
      title={lbl('Su asistente legal con IA', 'Your AI legal assistant')}
      lead={lbl(
        'Consulte leyes dominicanas, redacte contratos, calcule prestaciones laborales o ITBIS. Cita articulos con referencia al Codigo Civil, Trabajo, Penal, Tributario y mas.',
        'Query Dominican laws, draft contracts, compute labor benefits or ITBIS. It cites articles of the Civil, Labor, Penal and Tax codes and more.'
      )}
    >
      <div className="mt-4 rounded-md border border-bronze/20 bg-bronze/5 p-4 text-sm">
        <p className="text-foreground font-medium mb-1">
          💡 {lbl('Ejemplos que puede pedir:', 'Things you can ask:')}
        </p>
        <ul className="text-muted-foreground space-y-1 text-xs">
          <li>• {lbl('¿Cual es el preaviso para un empleado con 3 anos?', 'What is the preaviso for an employee with 3 years of service?')}</li>
          <li>• {lbl('Redacta un contrato de alquiler bajo la Ley 4314', 'Draft a lease contract under Law 4314')}</li>
          <li>• {lbl('Calcula el ITBIS de RD$250,000 con retencion', 'Compute ITBIS on RD\$250,000 with withholding')}</li>
        </ul>
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        {lbl(
          'La IA siempre es una ayuda, no reemplaza su criterio profesional. Revise antes de firmar.',
          'AI is always an aid, not a replacement for your professional judgment. Review before signing.'
        )}
      </p>
    </StepShell>
  );

  const step5 = (
    <StepShell
      icon={<CheckCircle2 className="w-12 h-12 text-emerald" />}
      title={lbl('¡Esta listo para empezar!', `You're ready to go!`)}
      lead={lbl(
        'Tiene 14 dias de prueba. Configure su tarjeta en cualquier momento desde Ajustes → Plan y Pagos. Si necesita ayuda, busque el icono de pregunta en la esquina.',
        'You have a 14-day trial. Configure your card anytime from Settings → Plan & Billing. If you need help, look for the question icon in the corner.'
      )}
    >
      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
        <TipCard icon={CreditCard} title={lbl('Plan y pagos', 'Plan & billing')} sub={lbl('Ajustes → Plan', 'Settings → Billing')} />
        <TipCard icon={Shield} title={lbl('Seguridad 2FA', '2FA security')} sub={lbl('Ajustes → Seguridad', 'Settings → Security')} />
        <TipCard icon={Mail} title={lbl('Integrar correo', 'Connect email')} sub={lbl('Integraciones', 'Integrations')} />
        <TipCard icon={FileText} title={lbl('Plantillas IA', 'AI templates')} sub={lbl('Plantillas legales', 'Legal templates')} />
      </div>
    </StepShell>
  );

  const steps = [step0, step1, step2, step3, step4, step5];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        <div className="bg-gradient-to-br from-navy via-navy to-navy-dark p-6 pb-4">
          <DialogHeader>
            <DialogTitle className="sr-only">Onboarding</DialogTitle>
            <DialogDescription className="sr-only">Initial tour</DialogDescription>
          </DialogHeader>
          {/* Progress bar */}
          <div className="flex gap-1.5 mb-0">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? 'bg-bronze' : 'bg-white/20'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="p-8 min-h-[340px]">
          {steps[step]}
        </div>

        <div className="px-8 pb-6 pt-2 flex items-center justify-between gap-3 border-t border-border">
          <button
            type="button"
            onClick={finish}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            disabled={saving}
          >
            <SkipForward className="w-4 h-4" />
            {lbl('Saltar tour', 'Skip tour')}
          </button>

          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" onClick={prev} disabled={saving}>
                {lbl('Atras', 'Back')}
              </Button>
            )}
            {step < totalSteps - 1 ? (
              <Button
                onClick={step === 1 && firmName.trim() ? saveFirmName : next}
                className="btn-accent gap-2"
                disabled={saving}
              >
                {lbl('Continuar', 'Continue')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={async () => { await finish(); navigate('/dashboard/cases'); }}
                className="btn-accent gap-2"
                disabled={saving}
              >
                {saving ? lbl('Guardando...', 'Saving...') : lbl('Comenzar', 'Get started')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StepShell({ icon, title, lead, children }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="shrink-0">{icon}</div>
        <h2 className="text-2xl font-serif font-bold text-foreground">{title}</h2>
      </div>
      <p className="text-muted-foreground leading-relaxed">{lead}</p>
      {children}
    </div>
  );
}

function Highlight({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 rounded-md border border-border bg-card">
      <Icon className="w-4 h-4 text-bronze shrink-0" />
      <span>{text}</span>
    </div>
  );
}

function Pill({ text }) {
  return (
    <div className="text-center py-2 px-3 rounded-full bg-secondary border border-border text-foreground font-medium">
      {text}
    </div>
  );
}

function TipCard({ icon: Icon, title, sub }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-bronze" />
        <span className="font-medium text-foreground text-sm">{title}</span>
      </div>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}
